import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Search, Filter, MapPin, Target, Award, Calendar, Leaf, DollarSign, Eye, ShoppingCart } from 'lucide-react';
import { useOptimisticRegistry } from '@/hooks/useOptimisticRegistry';
import { ProjectDeveloperDetailView } from './ProjectDeveloperDetailView';

interface ProjectCardProps {
  project: any;
  onView: (project: any) => void;
  onPurchase: (project: any) => void;
}

const ProjectCard = ({ project, onView, onPurchase }: ProjectCardProps) => {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  return (
    <Card className="group hover:shadow-lg transition-all duration-300 border-border/50 hover:border-primary/20">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg font-semibold mb-2 group-hover:text-primary transition-colors">
              {project.name}
            </CardTitle>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <MapPin className="w-4 h-4" />
              <span>{project.location}</span>
            </div>
            <Badge className={`text-xs ${getStatusColor(project.status)}`}>
              {project.status}
            </Badge>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        <div className="space-y-3">
          {/* Project Goal */}
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">Goal:</span>
            <span className="text-sm text-muted-foreground">{project.type}</span>
          </div>

          {/* Annual Carbon Footprint Offset */}
          <div className="flex items-center gap-2">
            <Leaf className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium">Annual Offset:</span>
            <span className="text-sm text-muted-foreground">
              {formatNumber(project.credits_issued)} tCO2e
            </span>
          </div>

          {/* Verification */}
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium">Audit Agency:</span>
            <span className="text-sm text-muted-foreground">
              {project.methodology || 'Verra'}
            </span>
          </div>

          {/* Price */}
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium">Price:</span>
            <span className="text-sm font-semibold text-green-600">
              ${(Math.random() * 50 + 10).toFixed(2)}/tCO2e
            </span>
          </div>

          <Separator className="my-3" />

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onView(project)}
              className="flex-1 gap-2"
            >
              <Eye className="w-4 h-4" />
              View Details
            </Button>
            <Button
              size="sm"
              onClick={() => onPurchase(project)}
              className="flex-1 gap-2"
            >
              <ShoppingCart className="w-4 h-4" />
              Purchase
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const EntityProjectBrowser = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('all-locations');
  const [selectedGoal, setSelectedGoal] = useState('all-goals');
  const [selectedAuditAgency, setSelectedAuditAgency] = useState('all-agencies');
  const [selectedOffsetRange, setSelectedOffsetRange] = useState('all-ranges');
  const [showUpcomingAuctions, setShowUpcomingAuctions] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  
  const { projects, isInitialLoading, error } = useOptimisticRegistry();

  // Filter projects based on search and filters
  const filteredProjects = useMemo(() => {
    let filtered = projects;

    if (searchTerm) {
      filtered = filtered.filter(project =>
        project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.developer.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedLocation && selectedLocation !== 'all-locations') {
      filtered = filtered.filter(project =>
        project.location.toLowerCase().includes(selectedLocation.toLowerCase())
      );
    }

    if (selectedGoal && selectedGoal !== 'all-goals') {
      filtered = filtered.filter(project =>
        project.type.toLowerCase().includes(selectedGoal.toLowerCase())
      );
    }

    if (selectedAuditAgency && selectedAuditAgency !== 'all-agencies') {
      filtered = filtered.filter(project =>
        project.methodology?.toLowerCase().includes(selectedAuditAgency.toLowerCase())
      );
    }

    if (selectedOffsetRange && selectedOffsetRange !== 'all-ranges') {
      const [min, max] = selectedOffsetRange.split('-').map(Number);
      filtered = filtered.filter(project => {
        const offset = project.credits_issued;
        return offset >= min && (!max || offset <= max);
      });
    }

    return filtered;
  }, [projects, searchTerm, selectedLocation, selectedGoal, selectedAuditAgency, selectedOffsetRange]);

  const handleViewProject = (project: any) => {
    setSelectedProject(project);
  };

  const handlePurchaseProject = (project: any) => {
    console.log('Purchase project:', project);
    // TODO: Open purchase dialog
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedLocation('all-locations');
    setSelectedGoal('all-goals');
    setSelectedAuditAgency('all-agencies');
    setSelectedOffsetRange('all-ranges');
    setShowUpcomingAuctions(false);
  };

  const handleBackToProjects = () => {
    setSelectedProject(null);
  };

  if (isInitialLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading available projects...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-destructive">Error loading projects. Please try again.</p>
        </div>
      </div>
    );
  }

  // Show detailed project view if a project is selected
  if (selectedProject) {
    return (
      <ProjectDeveloperDetailView 
        project={selectedProject} 
        onBack={handleBackToProjects}
      />
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">Carbon Credit Projects</h1>
        <p className="text-muted-foreground">Browse and purchase verified carbon credit projects</p>
      </div>

      {/* Search and Filters */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Search & Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search projects by name, location, or developer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Filter Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Location</label>
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger>
                  <SelectValue placeholder="All locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all-locations">All locations</SelectItem>
                  <SelectItem value="brazil">Brazil</SelectItem>
                  <SelectItem value="indonesia">Indonesia</SelectItem>
                  <SelectItem value="kenya">Kenya</SelectItem>
                  <SelectItem value="peru">Peru</SelectItem>
                  <SelectItem value="usa">United States</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Goal</label>
              <Select value={selectedGoal} onValueChange={setSelectedGoal}>
                <SelectTrigger>
                  <SelectValue placeholder="All goals" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all-goals">All goals</SelectItem>
                  <SelectItem value="reforestation">Reforestation</SelectItem>
                  <SelectItem value="renewable">Renewable Energy</SelectItem>
                  <SelectItem value="cookstoves">Clean Cookstoves</SelectItem>
                  <SelectItem value="avoided">Avoided Deforestation</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Audit Agency</label>
              <Select value={selectedAuditAgency} onValueChange={setSelectedAuditAgency}>
                <SelectTrigger>
                  <SelectValue placeholder="All agencies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all-agencies">All agencies</SelectItem>
                  <SelectItem value="verra">Verra (VCS)</SelectItem>
                  <SelectItem value="gold">Gold Standard</SelectItem>
                  <SelectItem value="climate">Climate Action Reserve</SelectItem>
                  <SelectItem value="american">American Carbon Registry</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Carbon Offset Range</label>
              <Select value={selectedOffsetRange} onValueChange={setSelectedOffsetRange}>
                <SelectTrigger>
                  <SelectValue placeholder="All ranges" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all-ranges">All ranges</SelectItem>
                  <SelectItem value="0-1000">0 - 1,000 tCO2e</SelectItem>
                  <SelectItem value="1000-10000">1,000 - 10,000 tCO2e</SelectItem>
                  <SelectItem value="10000-100000">10,000 - 100,000 tCO2e</SelectItem>
                  <SelectItem value="100000-999999">100,000+ tCO2e</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center">
            <Button variant="outline" onClick={clearFilters}>
              Clear All Filters
            </Button>
            <div className="text-sm text-muted-foreground">
              Showing {filteredProjects.length} of {projects.length} projects
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProjects.map((project) => (
          <ProjectCard
            key={project.registry_id}
            project={project}
            onView={handleViewProject}
            onPurchase={handlePurchaseProject}
          />
        ))}
      </div>

      {/* No Results */}
      {filteredProjects.length === 0 && (
        <Card className="text-center py-12">
          <CardContent>
            <Leaf className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Projects Found</h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your search criteria or clearing filters
            </p>
            <Button onClick={clearFilters}>Clear Filters</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};